// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { IApplicationsStatus } from "@/types/applications";
import { convertToCamelCase } from "./UtilsApprovals";
export var EIdentityMethod;
(function (EIdentityMethod) {
    EIdentityMethod["VERIFIED"] = "VERIFIED";
    EIdentityMethod["MANUAL_ENTRY"] = "MANUAL_ENTRY";
})(EIdentityMethod || (EIdentityMethod = {}));
export var EFieldMatch;
(function (EFieldMatch) {
    EFieldMatch["MATCHES"] = "MATCHES";
    EFieldMatch["DIFFERS"] = "DIFFERS";
})(EFieldMatch || (EFieldMatch = {}));
export var EDifferingFields;
(function (EDifferingFields) {
    EDifferingFields["NONE"] = "none";
    EDifferingFields["EXACTLY_1"] = "exactly_1";
    EDifferingFields["AT_MOST_1"] = "at_most_1";
    EDifferingFields["EXACTLY_2"] = "exactly_2";
    EDifferingFields["AT_MOST_2"] = "at_most_2";
    EDifferingFields["AT_LEAST_3"] = "at_least_3";
})(EDifferingFields || (EDifferingFields = {}));
export var EMatrixReason;
(function (EMatrixReason) {
    EMatrixReason["NO_VOTER"] = "NO_VOTER";
    EMatrixReason["ALREADY_APPROVED"] = "ALREADY_APPROVED";
    EMatrixReason["INSUFFICIENT_INFORMATION"] = "INSUFFICIENT_INFORMATION";
    EMatrixReason["IDENTITY_NOT_VERIFIED"] = "IDENTITY_NOT_VERIFIED";
    EMatrixReason["OTHER"] = "OTHER";
})(EMatrixReason || (EMatrixReason = {}));
export var EMatrixSource;
(function (EMatrixSource) {
    EMatrixSource["BUILT_IN"] = "BUILT_IN";
    EMatrixSource["SAVED"] = "SAVED";
})(EMatrixSource || (EMatrixSource = {}));
export var EMatrixError;
(function (EMatrixError) {
    EMatrixError["NO_COMPARED_FIELDS"] = "NO_COMPARED_FIELDS";
    EMatrixError["DUPLICATE_COMPARED_FIELD"] = "DUPLICATE_COMPARED_FIELD";
    EMatrixError["UNKNOWN_FIELD"] = "UNKNOWN_FIELD";
    EMatrixError["ACCEPTS_MANUAL_ENTRY"] = "ACCEPTS_MANUAL_ENTRY";
    EMatrixError["ACCEPTS_ALREADY_ENROLLED"] = "ACCEPTS_ALREADY_ENROLLED";
    EMatrixError["ACCEPTS_WITHOUT_VOTER"] = "ACCEPTS_WITHOUT_VOTER";
    EMatrixError["OTHERWISE_ACCEPTS"] = "OTHERWISE_ACCEPTS";
    EMatrixError["MISSING_REASON"] = "MISSING_REASON";
    EMatrixError["UNEXPECTED_REASON"] = "UNEXPECTED_REASON";
    /** Only the editor refuses it: a rule without conditions hides the rules below it. */
    EMatrixError["NO_CONDITIONS"] = "NO_CONDITIONS";
})(EMatrixError || (EMatrixError = {}));
var isRecord = function (value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};
var isMember = function (values, value) {
    return Object.values(values).some(function (candidate) { return candidate === value; });
};
export function readConditions(value) {
    if (!isRecord(value)) {
        return {};
    }
    var conditions = {};
    if (isMember(EIdentityMethod, value.identity)) {
        conditions.identity = value.identity;
    }
    if (typeof value.voter_found === "boolean") {
        conditions.voter_found = value.voter_found;
    }
    if (typeof value.already_enrolled === "boolean") {
        conditions.already_enrolled = value.already_enrolled;
    }
    if (typeof value.valid_id === "string" && value.valid_id) {
        conditions.valid_id = value.valid_id;
    }
    if (isMember(EDifferingFields, value.differing)) {
        conditions.differing = value.differing;
    }
    if (isRecord(value.fields)) {
        var fields_1 = {};
        Object.entries(value.fields).forEach(function (_a) {
            var field = _a[0], result = _a[1];
            if (isMember(EFieldMatch, result)) {
                fields_1[field] = result;
            }
        });
        if (Object.keys(fields_1).length > 0) {
            conditions.fields = fields_1;
        }
    }
    return conditions;
}
function readOutcome(value) {
    if (!isRecord(value) || !isMember(IApplicationsStatus, value.decision)) {
        return null;
    }
    return isMember(EMatrixReason, value.reason)
        ? { decision: value.decision, reason: value.reason }
        : { decision: value.decision };
}
/** Reads a matrix as the backend returns it; empty when it isn't one. */
export function readMatrix(value) {
    if (!isRecord(value) || !Array.isArray(value.compared_fields) || !Array.isArray(value.rules)) {
        return null;
    }
    var otherwise = readOutcome(value.otherwise);
    if (!otherwise) {
        return null;
    }
    var rules = [];
    for (var _i = 0, _a = value.rules; _i < _a.length; _i++) {
        var rule = _a[_i];
        var then = isRecord(rule) ? readOutcome(rule.then) : null;
        if (!isRecord(rule) || !then) {
            return null;
        }
        rules.push({ when: readConditions(rule.when), then: then });
    }
    return {
        compared_fields: value.compared_fields.filter(function (field) { return typeof field === "string"; }),
        rules: rules,
        otherwise: otherwise,
    };
}
/** Drops the reason of an accepting outcome, which has none. */
function cleanOutcome(outcome) {
    return outcome.decision === IApplicationsStatus.ACCEPTED || !outcome.reason
        ? { decision: outcome.decision }
        : { decision: outcome.decision, reason: outcome.reason };
}
/** Keeps only the conditions that are set, on fields the matrix compares. */
export function cleanConditions(when, comparedFields) {
    var conditions = readConditions(when);
    if (conditions.fields) {
        var fields = Object.fromEntries(Object.entries(conditions.fields).filter(function (_a) {
            var field = _a[0];
            return comparedFields.includes(field);
        }));
        if (Object.keys(fields).length > 0) {
            conditions.fields = fields;
        }
        else {
            delete conditions.fields;
        }
    }
    return conditions;
}
/** The matrix as the backend accepts it: no empty conditions and no unknown keys. */
export function cleanMatrix(matrix) {
    var comparedFields = matrix.compared_fields
        .map(function (field) { return field.trim(); })
        .filter(function (field, index, fields) { return field && fields.indexOf(field) === index; });
    return {
        compared_fields: comparedFields,
        rules: matrix.rules.map(function (rule) { return ({
            when: cleanConditions(rule.when, comparedFields),
            then: cleanOutcome(rule.then),
        }); }),
        otherwise: cleanOutcome(matrix.otherwise),
    };
}
export var sameMatrix = function (a, b) {
    return JSON.stringify(sortedKeys(cleanMatrix(a))) === JSON.stringify(sortedKeys(cleanMatrix(b)));
};
function sortedKeys(value) {
    if (Array.isArray(value)) {
        return value.map(sortedKeys);
    }
    if (isRecord(value)) {
        return Object.fromEntries(Object.keys(value)
            .sort()
            .map(function (key) { return [key, sortedKeys(value[key])]; }));
    }
    return value;
}
/** Why a rule can't be applied in the editor. `isOtherwise` is the last rule. */
export function validateRule(rule, isOtherwise) {
    if (isOtherwise === void 0) { isOtherwise = false; }
    var errors = [];
    if (!isOtherwise && Object.keys(readConditions(rule.when)).length === 0) {
        errors.push(EMatrixError.NO_CONDITIONS);
    }
    if (rule.then.decision === IApplicationsStatus.ACCEPTED) {
        if (isOtherwise) {
            return [EMatrixError.OTHERWISE_ACCEPTS];
        }
        if (rule.when.identity === EIdentityMethod.MANUAL_ENTRY) {
            errors.push(EMatrixError.ACCEPTS_MANUAL_ENTRY);
        }
        if (rule.when.already_enrolled === true) {
            errors.push(EMatrixError.ACCEPTS_ALREADY_ENROLLED);
        }
        if (rule.when.voter_found === false) {
            errors.push(EMatrixError.ACCEPTS_WITHOUT_VOTER);
        }
    }
    else if (!rule.then.reason) {
        errors.push(EMatrixError.MISSING_REASON);
    }
    return errors;
}
/** Every rule's errors, with the rule's position starting at 1 (empty for the last rule). */
export function validateMatrix(matrix) {
    var clean = cleanMatrix(matrix);
    var errors = [];
    if (clean.compared_fields.length === 0) {
        errors.push({ code: EMatrixError.NO_COMPARED_FIELDS, rule: null });
    }
    clean.rules.forEach(function (rule, index) {
        validateRule(rule).forEach(function (code) { return errors.push({ code: code, rule: index + 1 }); });
    });
    validateRule({ when: {}, then: clean.otherwise }, true).forEach(function (code) {
        return errors.push({ code: code, rule: null });
    });
    return errors;
}
export var addRule = function (matrix, rule) { return (__assign(__assign({}, matrix), { rules: __spreadArray(__spreadArray([], matrix.rules, true), [rule], false) })); };
export var replaceRule = function (matrix, index, rule) { return (__assign(__assign({}, matrix), { rules: matrix.rules.map(function (current, position) { return (position === index ? rule : current); }) })); };
export var deleteRule = function (matrix, index) { return (__assign(__assign({}, matrix), { rules: matrix.rules.filter(function (_, position) { return position !== index; }) })); };
/** Moves a rule one position up (-1) or down (1); unchanged at either end. */
export function moveRule(matrix, index, offset) {
    var _a;
    var target = index + offset;
    if (index < 0 || index >= matrix.rules.length || target < 0 || target >= matrix.rules.length) {
        return matrix;
    }
    var rules = __spreadArray([], matrix.rules, true);
    _a = [rules[target], rules[index]], rules[index] = _a[0], rules[target] = _a[1];
    return __assign(__assign({}, matrix), { rules: rules });
}
/** Changes the compared fields, dropping the conditions on fields no longer compared. */
export var withComparedFields = function (matrix, fields) {
    return cleanMatrix(__assign(__assign({}, matrix), { compared_fields: fields }));
};
/** "dateOfBirth" as "Date Of Birth", for fields the user profile doesn't name. */
export var humanizeField = function (field) {
    return field
        .replace(/[._-]+/g, " ")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .trim()
        .replace(/\b\w/g, function (letter) { return letter.toUpperCase(); });
};
var KEY = "approvalsScreen.matrix";
/** One line per condition, in the order the dialog shows them. */
export function conditionLabels(when, t, fieldLabel) {
    var _a;
    if (fieldLabel === void 0) { fieldLabel = humanizeField; }
    var labels = [];
    if (when.identity) {
        labels.push(t("".concat(KEY, ".conditions.identity.").concat(when.identity)));
    }
    if (when.voter_found !== undefined) {
        labels.push(t("".concat(KEY, ".conditions.voterFound.").concat(when.voter_found)));
    }
    if (when.already_enrolled !== undefined) {
        labels.push(t("".concat(KEY, ".conditions.alreadyEnrolled.").concat(when.already_enrolled)));
    }
    if (when.valid_id) {
        labels.push(t("".concat(KEY, ".conditions.validId"), { id: t(when.valid_id) }));
    }
    if (when.differing) {
        labels.push(t("".concat(KEY, ".conditions.differing.").concat(when.differing)));
    }
    Object.entries((_a = when.fields) !== null && _a !== void 0 ? _a : {}).forEach(function (_a) {
        var field = _a[0], result = _a[1];
        labels.push(t("".concat(KEY, ".conditions.field.").concat(result), { field: fieldLabel(field) }));
    });
    return labels.length > 0 ? labels : [t("".concat(KEY, ".conditions.any"))];
}
/** "When exactly 1 detail differs and embassy differs, approve the enrollment automatically." */
export function ruleSentence(rule, isOtherwise, t, fieldLabel) {
    if (fieldLabel === void 0) { fieldLabel = humanizeField; }
    var outcome = t("".concat(KEY, ".outcomeSentence.").concat(rule.then.decision));
    if (isOtherwise) {
        return t("".concat(KEY, ".sentenceOtherwise"), { outcome: outcome });
    }
    if (Object.keys(readConditions(rule.when)).length === 0) {
        return t("".concat(KEY, ".sentenceEmpty"));
    }
    var when = conditionLabels(rule.when, t, fieldLabel)
        .map(function (label) { return label.charAt(0).toLowerCase() + label.slice(1); })
        .join(t("".concat(KEY, ".and")));
    return t("".concat(KEY, ".sentence"), { when: when, outcome: outcome });
}
var sameRule = function (a, b) {
    return JSON.stringify(sortedKeys(a)) === JSON.stringify(sortedKeys(b));
};
/** Whether every rule of `part` is in `whole`, in the same order. */
function isSubsequence(part, whole) {
    var next = 0;
    whole.forEach(function (rule) {
        if (next < part.length && sameRule(part[next], rule)) {
            next += 1;
        }
    });
    return next === part.length;
}
/** The positions, starting at 1, of the rules of `whole` that `part` lacks. */
function missingFrom(part, whole) {
    var missing = [];
    var next = 0;
    whole.forEach(function (rule, index) {
        if (next < part.length && sameRule(part[next], rule)) {
            next += 1;
        }
        else {
            missing.push(index + 1);
        }
    });
    return missing;
}
/** What saving would change, one line each, for the unsaved bar and the save dialog. */
export function matrixChanges(savedMatrix, draftMatrix, t, fieldLabel) {
    if (fieldLabel === void 0) { fieldLabel = humanizeField; }
    var saved = cleanMatrix(savedMatrix);
    var draft = cleanMatrix(draftMatrix);
    var changes = [];
    var outcome = function (rule) { return t("".concat(KEY, ".outcomeShort.").concat(rule.then.decision)); };
    if (JSON.stringify(saved.compared_fields) !== JSON.stringify(draft.compared_fields)) {
        changes.push(t("".concat(KEY, ".change.compared")));
    }
    var sorted = function (rules) {
        return rules.map(function (rule) { return JSON.stringify(sortedKeys(rule)); }).sort();
    };
    if (saved.rules.length < draft.rules.length && isSubsequence(saved.rules, draft.rules)) {
        missingFrom(saved.rules, draft.rules).forEach(function (number) {
            return changes.push(t("".concat(KEY, ".change.added"), { number: number }));
        });
    }
    else if (saved.rules.length > draft.rules.length && isSubsequence(draft.rules, saved.rules)) {
        missingFrom(draft.rules, saved.rules).forEach(function (number) {
            return changes.push(t("".concat(KEY, ".change.removed"), {
                text: conditionLabels(saved.rules[number - 1].when, t, fieldLabel).join(", "),
            }));
        });
    }
    else if (saved.rules.length === draft.rules.length &&
        JSON.stringify(sorted(saved.rules)) === JSON.stringify(sorted(draft.rules))) {
        if (!saved.rules.every(function (rule, index) { return sameRule(rule, draft.rules[index]); })) {
            changes.push(t("".concat(KEY, ".change.moved")));
        }
    }
    else {
        draft.rules.forEach(function (rule, index) {
            var number = index + 1;
            var before = saved.rules[index];
            if (!before) {
                changes.push(t("".concat(KEY, ".change.added"), { number: number }));
            }
            else if (!sameRule(before, rule)) {
                changes.push(JSON.stringify(sortedKeys(before.when)) ===
                    JSON.stringify(sortedKeys(rule.when)) &&
                    before.then.decision !== rule.then.decision
                    ? t("".concat(KEY, ".change.decision"), {
                        number: number,
                        from: outcome(before),
                        to: outcome(rule),
                    })
                    : t("".concat(KEY, ".change.edited"), { number: number }));
            }
        });
        saved.rules.slice(draft.rules.length).forEach(function (rule) {
            return changes.push(t("".concat(KEY, ".change.removed"), {
                text: conditionLabels(rule.when, t, fieldLabel).join(", "),
            }));
        });
    }
    if (JSON.stringify(sortedKeys(saved.otherwise)) !== JSON.stringify(sortedKeys(draft.otherwise))) {
        changes.push(t("".concat(KEY, ".change.otherwise")));
    }
    return changes;
}
/** The enrollment the test panel starts from: a verified voter whose fields all match. */
export var defaultEnrollment = function (comparedFields) { return ({
    identity: EIdentityMethod.VERIFIED,
    voter_found: true,
    already_enrolled: false,
    valid_id: null,
    fields: Object.fromEntries(comparedFields.map(function (field) { return [field, EFieldMatch.MATCHES]; })),
}); };
/** The enrollment restricted to the fields the matrix compares now. */
export var enrollmentFor = function (enrollment, comparedFields) { return (__assign(__assign({}, enrollment), { fields: Object.fromEntries(comparedFields.map(function (field) { var _a; return [field, (_a = enrollment.fields[field]) !== null && _a !== void 0 ? _a : EFieldMatch.MATCHES]; })) })); };
/**
 * Names compared fields as the event's user profile does. `attributeLabel`
 * turns a profile display name into the text to translate.
 */
export var profileFieldLabel = function (attributes, t, attributeLabel) {
    return function (field) {
        var _a;
        var attribute = attributes.find(function (_a) {
            var name = _a.name;
            return name && convertToCamelCase(name) === convertToCamelCase(field);
        });
        return (attribute === null || attribute === void 0 ? void 0 : attribute.name)
            ? t(attributeLabel((_a = attribute.display_name) !== null && _a !== void 0 ? _a : attribute.name))
            : humanizeField(field);
    };
};
/**
 * The translation key of an application's rejection reason. The matrix
 * stores a reason by its name; an officer's manual rejection by its slug.
 */
export var rejectionReasonKey = function (reason) {
    return Object.values(EMatrixReason).includes(reason !== null && reason !== void 0 ? reason : "")
        ? "approvalsScreen.matrix.reasons.".concat(reason)
        : "approvalsScreen.reject.reasons.".concat(reason !== null && reason !== void 0 ? reason : "undefined");
};
